/**
 * Marketplace Algorithm Compliance Auditor
 * -----------------------------------------
 * Deterministic, dependency-free scoring of product photos for the three
 * suppression triggers used by Amazon / Etsy / Shopify search algorithms:
 *
 *   a) Pure White Perimeter Score  - % of border pixels that are exactly RGB(255,255,255)
 *   b) Product Frame Fill Ratio    - % of the canvas occupied by the non-white product box
 *   c) Mobile Thumbnail Pop Score  - RMS contrast + Sobel edge sharpness of a 150x150 downsample
 *
 * Every function operates on raw RGBA pixel buffers (`Uint8ClampedArray`) so the
 * whole module is testable in Node without a DOM.
 */

export interface BoundingBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
}

export interface AuditReport {
  /** 0-100 % of perimeter pixels that are strictly RGB(255,255,255) */
  perimeterWhiteScore: number;
  /** 0-100 % of total canvas area occupied by the detected product box */
  fillRatio: number;
  /** 0-100 mobile-thumbnail contrast/sharpness score */
  popScore: number;
  /** Component metrics behind the pop score (handy for UI breakdowns) */
  rmsContrast: number;
  edgeSharpness: number;
  /** True when all three marketplace thresholds pass */
  isAmazonCompliant: boolean;
  boundingBox: BoundingBox;
}

/** Tunable compliance thresholds, shared between the auditor and the UI. */
export const COMPLIANCE_THRESHOLDS = {
  /** Amazon wants a practically pure-white perimeter */
  perimeterWhiteMin: 99,
  /** Amazon main-image guidance: product should fill >= 85% of the frame */
  fillRatioMin: 85,
  /** Reject "washed out / mushy" mobile thumbnails */
  popScoreMin: 45,
  /** Target used by the Pro "auto-crop & pad" repair (spec: exact 86%) */
  autoFillTarget: 86
} as const;

/** Border ring width in pixels used for the perimeter score (1 = outer edge only). */
const PERIMETER_RING = 1;

/** Threshold used by the bounding-box detector: pixels at/below this on all channels count as background. */
export const DEFAULT_BACKGROUND_THRESHOLD = 250;

/** Threshold used by the "snap near-white" repair (spec: > 240 => pure #FFFFFF). */
export const NEAR_WHITE_THRESHOLD = 240;

/** Size of the mobile search thumbnail used for the pop score (spec: 150x150). */
export const MOBILE_THUMB_SIZE = 150;

function luminanceAt(pixels: Uint8ClampedArray, index: number): number {
  return 0.299 * pixels[index] + 0.587 * pixels[index + 1] + 0.114 * pixels[index + 2];
}

/**
 * % of border-ring pixels that are strictly RGB(255,255,255).
 * Returns 100 for degenerate buffers with no pixels.
 */
export function computePerimeterWhiteScore(
  pixels: Uint8ClampedArray,
  width: number,
  height: number
): number {
  if (width <= 0 || height <= 0) return 100;

  let borderPixelCount = 0;
  let pureWhiteCount = 0;
  const maxX = width - 1;
  const maxY = height - 1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const isPerimeter = x < PERIMETER_RING || x >= width - PERIMETER_RING || y < PERIMETER_RING || y >= height - PERIMETER_RING;
      if (!isPerimeter) continue;

      borderPixelCount++;
      const idx = (y * width + x) * 4;
      if (pixels[idx] === 255 && pixels[idx + 1] === 255 && pixels[idx + 2] === 255) {
        pureWhiteCount++;
      }
    }
  }
  void maxX;
  void maxY;

  return borderPixelCount > 0 ? (pureWhiteCount / borderPixelCount) * 100 : 100;
}

/**
 * Smallest axis-aligned box containing every non-background pixel.
 * Falls back to the full canvas when the image is effectively all background.
 */
export function findProductBoundingBox(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  threshold = DEFAULT_BACKGROUND_THRESHOLD
): BoundingBox {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const isNonWhite = pixels[idx] < threshold || pixels[idx + 1] < threshold || pixels[idx + 2] < threshold;
      if (!isNonWhite) continue;

      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }

  if (maxX < 0 || maxY < 0) {
    // Nothing detected - treat the whole canvas as the "product".
    return { minX: 0, minY: 0, maxX: width - 1, maxY: height - 1, width, height };
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX + 1,
    height: maxY - minY + 1
  };
}

/** Product box area as a percentage of the total canvas area. */
export function computeFillRatio(bbox: BoundingBox, totalWidth: number, totalHeight: number): number {
  const boxArea = bbox.width * bbox.height;
  const totalArea = totalWidth * totalHeight;
  return totalArea > 0 ? (boxArea / totalArea) * 100 : 0;
}

/**
 * True when at least one pixel counts as product (non-background).
 * `findProductBoundingBox` falls back to the full canvas when nothing is
 * detected, so repairs use this to tell "all background" from "real content".
 */
export function hasProductContent(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  threshold = DEFAULT_BACKGROUND_THRESHOLD
): boolean {
  const count = width * height;
  for (let i = 0; i < count; i++) {
    const idx = i * 4;
    if (pixels[idx] < threshold || pixels[idx + 1] < threshold || pixels[idx + 2] < threshold) {
      return true;
    }
  }
  return false;
}

/**
 * 1-click repair: every pixel whose channels are ALL strictly greater than the
 * threshold (default > 240) is snapped to pure #FFFFFF. Returns a new buffer.
 */
export function snapNearWhiteToPureWhite(pixels: Uint8ClampedArray, threshold = NEAR_WHITE_THRESHOLD): Uint8ClampedArray {
  const output = new Uint8ClampedArray(pixels);
  for (let i = 0; i < output.length; i += 4) {
    if (output[i] > threshold && output[i + 1] > threshold && output[i + 2] > threshold) {
      output[i] = 255;
      output[i + 1] = 255;
      output[i + 2] = 255;
    }
  }
  return output;
}

/** Reads the buffer as a grayscale luminance plane. */
export function computeLuminancePlane(pixels: Uint8ClampedArray, width: number, height: number): Float32Array {
  const count = width * height;
  const luminances = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    luminances[i] = luminanceAt(pixels, i * 4);
  }
  return luminances;
}

/** RMS contrast (standard deviation of luminance) of the pixel buffer. */
export function computeRmsContrast(pixels: Uint8ClampedArray, width: number, height: number): number {
  const count = width * height;
  if (count === 0) return 0;

  const luminances = computeLuminancePlane(pixels, width, height);
  let sum = 0;
  for (let i = 0; i < count; i++) sum += luminances[i];
  const mean = sum / count;

  let variance = 0;
  for (let i = 0; i < count; i++) {
    const diff = luminances[i] - mean;
    variance += diff * diff;
  }
  return Math.sqrt(variance / count);
}

/**
 * Mean Sobel gradient magnitude of the luminance plane - a direct proxy for
 * "does the product still have crisp edges at thumbnail size?".
 */
export function computeEdgeSharpness(pixels: Uint8ClampedArray, width: number, height: number): number {
  if (width < 3 || height < 3) return 0;
  const lum = computeLuminancePlane(pixels, width, height);

  let total = 0;
  let samples = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const tl = lum[i - width - 1];
      const tc = lum[i - width];
      const tr = lum[i - width + 1];
      const ml = lum[i - 1];
      const mr = lum[i + 1];
      const bl = lum[i + width - 1];
      const bc = lum[i + width];
      const br = lum[i + width + 1];

      const gx = tr + 2 * mr + br - (tl + 2 * ml + bl);
      const gy = bl + 2 * bc + br - (tl + 2 * tc + tr);
      total += Math.sqrt(gx * gx + gy * gy);
      samples++;
    }
  }
  return samples > 0 ? total / samples : 0;
}

/**
 * Mobile Thumbnail Pop Score (0-100).
 * Weighted blend of normalised RMS contrast (0-100) and normalised Sobel edge
 * sharpness (0-100). Flat / washed-out thumbnails score near 0, punchy product
 * shots with crisp edges score near 100.
 */
export function computePopScore(pixels: Uint8ClampedArray, width: number, height: number): { score: number; rmsContrast: number; edgeSharpness: number } {
  const rms = computeRmsContrast(pixels, width, height);
  const edges = computeEdgeSharpness(pixels, width, height);

  // 80 RMS luminance is "extremely high contrast" -> 100
  const rmsScore = Math.min(100, (rms / 80) * 100);
  // 45 mean Sobel magnitude is "razor sharp" -> 100
  const edgeScore = Math.min(100, (edges / 45) * 100);

  const score = Math.min(100, Math.round(0.6 * rmsScore + 0.4 * edgeScore));
  return {
    score,
    rmsContrast: Math.round(rms * 10) / 10,
    edgeSharpness: Math.round(edges * 10) / 10
  };
}

/** Runs the complete three-check audit on a raw RGBA buffer. */
export function auditImagePixels(pixels: Uint8ClampedArray, width: number, height: number): AuditReport {
  const perimeterWhiteScore = computePerimeterWhiteScore(pixels, width, height);
  const boundingBox = findProductBoundingBox(pixels, width, height);
  const fillRatio = computeFillRatio(boundingBox, width, height);

  // Pop score is measured on a 150x150 downsample: exactly what a shopper sees
  // in the mobile search grid.
  const thumb = resizePixels(pixels, width, height, MOBILE_THUMB_SIZE, MOBILE_THUMB_SIZE);
  const pop = computePopScore(thumb.pixels, thumb.width, thumb.height);

  const isAmazonCompliant =
    perimeterWhiteScore >= COMPLIANCE_THRESHOLDS.perimeterWhiteMin &&
    fillRatio >= COMPLIANCE_THRESHOLDS.fillRatioMin &&
    pop.score >= COMPLIANCE_THRESHOLDS.popScoreMin;

  return {
    perimeterWhiteScore: round2(perimeterWhiteScore),
    fillRatio: round2(fillRatio),
    popScore: pop.score,
    rmsContrast: pop.rmsContrast,
    edgeSharpness: pop.edgeSharpness,
    isAmazonCompliant,
    boundingBox
  };
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Pure box-filter resize. Works for both down- and up-sampling deterministically. */
export function resizePixels(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  targetWidth: number,
  targetHeight: number
): { pixels: Uint8ClampedArray; width: number; height: number } {
  if (targetWidth <= 0 || targetHeight <= 0) {
    throw new Error('resizePixels: target dimensions must be positive');
  }
  if (width === targetWidth && height === targetHeight) {
    return { pixels: new Uint8ClampedArray(pixels), width, height };
  }

  const out = new Uint8ClampedArray(targetWidth * targetHeight * 4);

  for (let ty = 0; ty < targetHeight; ty++) {
    const sy0 = Math.floor((ty * height) / targetHeight);
    const sy1 = Math.max(sy0 + 1, Math.floor(((ty + 1) * height) / targetHeight));
    for (let tx = 0; tx < targetWidth; tx++) {
      const sx0 = Math.floor((tx * width) / targetWidth);
      const sx1 = Math.max(sx0 + 1, Math.floor(((tx + 1) * width) / targetWidth));

      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      let n = 0;
      for (let sy = sy0; sy < sy1 && sy < height; sy++) {
        for (let sx = sx0; sx < sx1 && sx < width; sx++) {
          const idx = (sy * width + sx) * 4;
          r += pixels[idx];
          g += pixels[idx + 1];
          b += pixels[idx + 2];
          a += pixels[idx + 3];
          n++;
        }
      }

      const o = (ty * targetWidth + tx) * 4;
      out[o] = Math.round(r / n);
      out[o + 1] = Math.round(g / n);
      out[o + 2] = Math.round(b / n);
      out[o + 3] = Math.round(a / n);
    }
  }

  return { pixels: out, width: targetWidth, height: targetHeight };
}
